<!--suppress HtmlUnknownTag -->
<vuejs3-todo>
    Ready
</vuejs3-todo>

<template>
    <section class="invite-page">
        <h1 v-if="isExpired">{{ i18n.feedBack.expired }}</h1>
        <h1 v-else-if="isAlreadyCompleted">{{ i18n.feedBack.alreadyCompleted }}</h1>
        <h1 v-else-if="isSuccessful">{{ i18n.feedBack.success }}</h1>
        <h1 v-else-if="error">{{ i18n._error }}</h1>
        <template v-else>
            <form @submit.prevent="step++"
                  autocomplete="off"
                  v-if="step === 1"
            >
                <h2>
                    <template v-if="$i18n.lang === 'fr'">
                        Bienvenue sur l'application Echoline !<br/>
                        Vous serez prêts à l'utiliser dans quelques clics !
                    </template>
                    <template v-else-if="$i18n.lang === 'de'">
                        Willkommen bei der Echoline-Anwendung!<br/>
                        In wenigen Klicks ist sie für den Einsatz bereit!
                    </template>
                    <template v-else>
                        Welcome to Echoline app!<br/>
                        You're a few clicks away from starting to use it!
                    </template>
                </h2>
                <input type="email"
                       disabled
                       :value="user.email"
                />
                <label>
                    <span>{{ i18n.label.firstname }}</span>
                    <input type="text"
                           v-model="user.firstname"
                           required
                           :class="isFirstnameValid ? 'valid' : 'invalid'"
                    />
                    <span :class="{strong: !isFirstnameValid}">{{ i18n.rules.firstname }}</span>
                </label>
                <label>
                    <span>{{ i18n.label.lastname }}</span>
                    <input type="text"
                           v-model="user.lastname"
                           required
                           :class="isFirstnameValid ? 'valid' : 'invalid'"
                    />
                    <span :class="{strong: !isLastnameValid}">{{ i18n.rules.lastname }}</span>
                </label>
                <label>
                    <span>{{ i18n.label.password }}</span>
                    <input type="password"
                           v-model="user.password"
                           required
                           :class="isPasswordValid ? 'valid' : 'invalid'"
                    />
                    <span :class="{strong: !isPasswordValid}">
                        <template v-if="$i18n.lang === 'fr'">
                            Au moins 8 caractères, au moins un numéro (0-9) et au moins un caractère spécial (<code>-?!@#$%^*</code>)
                        </template>
                        <template v-else-if="$i18n.lang === 'de'">
                            Es muss aus mindestens acht Zeichen bestehen und wenigstens eine Zahl (0-9) und ein Sonderzeichen (<code>-?!@#$%^*</code>) enthalten.
                        </template>
                        <template v-else>
                            At least 8 characters, at least one number (0-9) and at least one special character (<code>-?!@#$%^*</code>)
                        </template>
                    </span>
                </label>
                <label>
                    <span>{{ i18n.label.confirmedPassword }}</span>
                    <input type="password"
                           v-model="confirmedPassword"
                           required
                           :class="isPasswordConfirmed ? 'valid' : 'invalid'"
                    />
                    <span v-if="!isPasswordConfirmed"
                          class="strong"
                    >{{ i18n.passwordNotConfirmed }}</span>
                </label>
                <div>
                    <button type="submit"
                            :disabled="!canContinue"
                    >{{ i18n.submit.step1 }}</button>
                </div>
            </form>
            <form @submit.prevent="submit"
                  v-if="step === 2"
                  autocomplete="off"
            >
                <h2>{{ i18n.additionalData }}</h2>
                <fieldset :class="{invalid: !arePhoneNumberValid}">
                    <legend>{{ i18n.phoneNumbers }}</legend>
                    <label>
                        <span>{{ i18n.label.phoneNumber.landline }}</span>
                        <input type="text"
                               v-model="user.phoneNumber.landline"
                        />
                    </label>
                    <label>
                        <span>{{ i18n.label.phoneNumber.mobile }}</span>
                        <input type="text"
                               v-model="user.phoneNumber.mobile"
                        />
                    </label>
                    <p class="label-phone-numbers-rules"
                       :class="{strong: !arePhoneNumberValid}"
                    >{{ i18n.rules.phoneNumbers }}</p>
                </fieldset>
                <label>
                    <span>{{ i18n.label.function }}</span>
                    <input type="text"
                           v-model="user.function"
                           required
                           :class="isFunctionValid ? 'valid' : 'invalid'"
                    >
                    <span :class="{strong: !isFunctionValid}">{{ i18n.rules.function }}</span>
                </label>
                <div class="container-buttons-step2">
                    <a @click="step--">{{ i18n.back }}</a>
                    <button type="submit"
                            :disabled="!canBeSubmitted"
                    >{{ i18n.submit.step2 }}</button>
                </div>
            </form>
        </template>
    </section>
</template>

<script>
    import axios from "@/inside/axios";
    import User from "@/common/models/User";

    const i18n = {
        de: {
            additionalData: `
                Für den Kontakt zum Echoline-Team benötigen wir noch einige abschließende Informationen!
            `,
            back: "Einen Schritt zurück",
            feedBack: {
                alreadyCompleted: "Die Einladung ist nicht mehr gültig 😕 Sie haben Ihren Account schon erstellt !",
                expired: `
                    Die Einladung ist nicht mehr gültig 😕 Sie können von einem der aktuellen Echoline-Nutzer in Ihrem
                    Team eine neue Einladung anfordern!
                    `,
                success: "Willkommen bei der Echoline-Anwendung!"
            },
            intro: "Willkommen bei der Echoline-Anwendung! In wenigen Klicks ist sie für den Einsatz bereit!",
            label: {
                confirmedPassword: "Geben Sie Ihr Passwort zur Bestätigung erneut ein",
                firstname: "Bitte geben Sie Ihren Vornamen ein",
                function: "Welche Funktion haben Sie?",
                lastname: "Bitte geben Sie Ihren Namen ein",
                password: "Wählen Sie ein Passwort",
                phoneNumber: {
                    landline: "Festnetzrufnummer",
                    mobile: "Mobilrufnummer"
                }
            },
            passwordNotConfirmed: "Die beiden Passwörter sind nicht identisch",
            phoneNumbers: "Telefon",
            rules: {
                firstname: "Mindestens ein Zeichen",
                function: "Mindestens ein Zeichen",
                lastname: "Mindestens ein Zeichen",
                phoneNumbers: "Es muss eine der Rufnummern eingegeben werden."
            },
            submit: {
                step1: "Weiter",
                step2: "Abschließen"
            }
        },
        en: {
            additionalData: `
                To ease communication between you and Echoline team, we need some more (and last)
                information!
            `,
            back: "Back to previous step",
            feedBack: {
                alreadyCompleted: "Invite has expired 😕 You already created your account!",
                expired: "Invite has expired 😕 You can ask for a new invite to an Echoline user in your team!",
                success: "Welcome to Echoline app!"
            },
            intro: "Welcome to Echoline app! You're a few clicks away from starting to use it!",
            label: {
                confirmedPassword: "Confirm your password",
                firstname: "What's your firstname?",
                function: "What's your function?",
                lastname: "What's your lastname?",
                password: "Choose your password",
                phoneNumber: {
                    landline: "Phone number (landline)",
                    mobile: "Phone number (mobile)"
                }
            },
            passwordNotConfirmed: "The two passwords are not identical",
            phoneNumbers: "Phone number",
            rules: {
                firstname: "One character minimum",
                function: "One character minimum",
                lastname: "One character minimum",
                phoneNumbers: "At least one phone number must be filled in"
            },
            submit: {
                step1: "Continue",
                step2: "Complete"
            }
        },
        fr: {
            additionalData: `
                Pour faciliter les échanges avec l'équipe Echoline, nous avons besoin de quelques dernières
                informations !
            `,
            back: "Retour à l'étape précédente",
            feedBack: {
                alreadyCompleted: "L'invitation n'est plus valide 😕 Vous avez déjà créé votre compte !",
                expired: `
                    L'invitation n'est plus valide 😕 Vous pouvez demander une nouvelle invitation à l'un des
                    utilisateurs actuels d'Echoline dans votre équipe !
                `,
                success: "Bienvenue sur l'application Echoline !"
            },
            intro: "Bienvenue sur l'application Echoline ! Vous serez prêts à l'utiliser dans quelques clics !",
            label: {
                confirmedPassword: "Confirmez votre mot de passe",
                firstname: "Quel est votre prénom ?",
                function: "Quelle est votre fonction ?",
                lastname: "Quel est votre nom ?",
                password: "Choisissez votre mot de passe",
                phoneNumber: {
                    landline: "Téléphone fixe",
                    mobile: "Téléphone mobile"
                }
            },
            passwordNotConfirmed: "Les deux mots de passe ne correspondent pas",
            phoneNumbers: "Téléphone",
            rules: {
                firstname: "Un caractère minimum",
                function: "Un caractère minimum",
                lastname: "Un caractère minimum",
                phoneNumbers: "L'un des numéros doit être renseigné"
            },
            submit: {
                step1: "Continuer",
                step2: "Terminer"
            }
        }
    };

    export default {
        computed: {
            /**
             * Returns a boolean indicating whether phone numbers are valid.
             * @return {boolean}
             */
            arePhoneNumberValid() {
                return (
                    (
                        this.user.phoneNumber.mobile !== null
                        && this.user.phoneNumber.mobile.trim() !== ""
                    ) || (
                        this.user.phoneNumber.landline !== null
                        && this.user.phoneNumber.landline.trim() !== ""
                    )
                );
            },
            /**
             * Returns a boolean indicating whether user can continue to step 2.
             * @return {boolean}
             */
            canContinue() {
                return (
                    this.isFirstnameValid
                    && this.isLastnameValid
                    && this.isPasswordValid
                    && this.isPasswordConfirmed
                );
            },
            /**
             * Returns a boolean indicating whether invite can be completed.
             * @return {boolean}
             */
            canBeSubmitted() {
                return (
                    this.canContinue
                    && this.arePhoneNumberValid
                    && this.isFunctionValid
                );
            },
            i18n() {
                return this.$i18n.strings(i18n, this.$options.name);
            },
            /**
             * Returns a boolean indicating whether firstname is valid.
             * @return {boolean}
             */
            isFirstnameValid() {
                return (
                    this.user.firstname !== null
                    && this.user.firstname.trim() !== ""
                );
            },
            /**
             * Returns a boolean indicating whether function is valid.
             * @return {boolean}
             */
            isFunctionValid() {
                return (
                    this.user.function !== null
                    && this.user.function.trim() !== ""
                );
            },
            /**
             * Returns a boolean indicating whether lastname is valid.
             * @return {boolean}
             */
            isLastnameValid() {
                return (
                    this.user.lastname !== null
                    && this.user.lastname.trim() !== ""
                );
            },
            /**
             * Returns a boolean indicating whether passwords are matching.
             * @return {boolean}
             */
            isPasswordConfirmed() {
                return (
                    this.confirmedPassword !== null
                    && this.confirmedPassword.trim() === this.user.password
                );
            },
            /**
             * Returns a boolean indicating whether password respects validation rules.
             * @return {boolean}
             */
            isPasswordValid() {
                return User.passwordRegExp.test(this.user.password);
            }
        },
        created() {
            const url = new URL(document.location.href);

            if (!url.searchParams.has("token")) {
                this.$router.replace("/login");
                return;
            }

            this.token = url.searchParams.get("token");

            if (this.token !== "test") {
                const data = JSON.parse(atob(this.token.split(".")[1]));
                this.user.email = data.uid;
            }
        },
        data() {
            return {
                confirmedPassword: null,
                error: false,
                isAlreadyCompleted: false,
                isExpired: false,
                isSuccessful: false,
                step: 1,
                token: null,
                user: {
                    email: "sarah.connor@echoline.fr",
                    firstname: null,
                    function: null,
                    lastname: null,
                    password: null,
                    phoneNumber: {
                        landline: null,
                        mobile: null
                    }
                }
            };
        },
        methods: {
            /**
             * Completes invite through Echoline API.
             * @return {void}
             */
            submit() {
                if (!this.canBeSubmitted) {
                    return;
                }

                axios.post("/rpc", {
                    action: "customers.invite.complete",
                    params: {
                        token: this.token,
                        firstname: this.user.firstname,
                        lastname: this.user.lastname,
                        password: this.user.password,
                        phoneNumber: {
                            mobile: this.user.phoneNumber.mobile,
                            landline: this.user.phoneNumber.landline
                        },
                        function: this.user.function
                    }
                }, {
                    validateStatus: status => status === 200
                }).then((response) => {
                    this.isSuccessful = true;
                    setTimeout(() => {
                        window.location.href = `/outside.html?use=${response.data}#/login`;
                    }, 2000);
                }).catch((error) => {
                    if (
                        Object.prototype.hasOwnProperty.call(error, "response")
                        && error.response.resource === "error"
                    ) {
                        if (error.response.data.code === "11") {
                            this.isExpired = true;
                            return;
                        }

                        if (error.response.data.code === "12") {
                            this.isAlreadyCompleted = true;
                            return;
                        }
                    }

                    this.error = true;
                });
            }
        },
        name: "InvitePage"
    }
</script>

<style lang="scss">
    @import "../../colors";
    @import "../../mixins";
    @import "../../variables";

    .invite-page {
        display: flex;
        padding: 32px 0;
        box-sizing: border-box;
        min-height: 100vh;
        width: 100vw;
        align-items: center;
        flex-direction: column;
        justify-content: center;
        background-image: url("/img/outside_invite-page_background.svg");
        background-position: bottom right;
        background-repeat: no-repeat;
        background-size: 32%;

        form {
            width: 40%;
            background-color: white;
            box-shadow: 0 0 4px black;
            border-radius: 8px;
            box-sizing: border-box;
            padding: 0 32px;

            h2 {
                font-weight: $font-weight-bold;
                margin: 32px 0;
                line-height: 24px;
            }

            input {
                font-family: inherit;
                font-size: inherit;
                line-height: 24px;
                border: 1px solid $color-border;
                padding: 7px 11px;
                border-radius: 4px;
                box-sizing: border-box;
                width: 100%;

                &:focus,
                &:active {
                    outline: none;
                    border-color: $color-blue-medium;
                }

                &.invalid {
                    border-color: $color-red-dark;
                }

                &.valid {
                    border-color: $color-green-dark;
                }
            }

            label {
                margin: 16px 0;
                display: block;

                span {
                    font-size: 12px;
                    margin: 0 4px;
                    line-height: 24px;

                    &:first-child,
                    &.strong {
                        font-weight: $font-weight-bold;
                    }
                }
            }

            fieldset {
                border: 1px solid $color-border;
                padding: 8px;
                border-radius: 4px;
                margin: 0;

                label:first-of-type {
                    margin-top: 8px;
                }

                legend {
                    font-size: 12px;
                    padding: 0 4px;
                }

                &.invalid {
                    border-color: $color-red-dark;
                }

                p {
                    font-size: 12px;
                    margin-bottom: 0;
                    padding: 0 4px;

                    &.strong {
                        font-weight: $font-weight-bold;
                    }
                }
            }

            div {
                display: flex;
                justify-content: right;
                line-height: 40px;
                margin: 32px 0;

                &.container-buttons-step2 {
                    justify-content: space-between;

                    a {
                        color: $color-font-light;
                        text-decoration: none;
                        margin-left: 13px;
                        cursor: pointer;
                    }
                }

                button {
                    line-height: 38px;
                    border: 1px solid $color-mint;
                    background-color: $color-mint;
                    color: white;
                    font-family: inherit;
                    font-size: inherit;
                    padding: 0 12px;
                    border-radius: 4px;
                    cursor: pointer;
                    @include transition(color, background-color);

                    &:hover {
                        color: $color-mint;
                        background-color: transparent;
                    }
                }
            }
        }

        h1 {
            font-weight: $font-weight-bold;
        }
    }
</style>
